class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.hoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.closeAnimationTimeout = null;
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.boundDocumentPointerMove = this.onDocumentPointerMove.bind(this);

    this.addEventListener('pointerleave', this.onPointerLeave.bind(this));
    this.content.addEventListener('pointerenter', this.onContentPointerEnter.bind(this));
    this.addEventListener('pointermove', this.onMenuPointerMove.bind(this));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('pointermove', this.boundDocumentPointerMove);
  }

  onSummaryClick(event) {
    if (!this.hoverMedia.matches || !this.summary.dataset.menuUrl) return;

    event.preventDefault();
    window.location.assign(this.summary.dataset.menuUrl);
  }

  openMenu() {
    if (!this.hoverMedia.matches) return;

    this.cancelClose();
    this.header?.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this) menu.close(true);
    });

    const headerBottom = this.header?.getBoundingClientRect().bottom || 0;
    const summaryBottom = this.summary.getBoundingClientRect().bottom;
    this.style.setProperty('--header-menu-gap', `${Math.max(0, headerBottom - summaryBottom)}px`);

    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
    document.addEventListener('pointermove', this.boundDocumentPointerMove, { passive: true });
  }

  queueOpen() {
    if (!this.hoverMedia.matches || this.mainDetailsToggle.open) return;

    this.openMenu();
  }

  cancelClose() {
    clearTimeout(this.closeAnimationTimeout);
    this.classList.remove('menu-closing');

    if (this.mainDetailsToggle.open) {
      this.summary.setAttribute('aria-expanded', true);
      document.addEventListener('pointermove', this.boundDocumentPointerMove, { passive: true });
    }
  }

  onContentPointerEnter() {
    if (this.classList.contains('menu-closing')) return;

    this.cancelClose();
  }

  onPointerLeave(event) {
    if (!this.hoverMedia.matches) return;

    this.onDocumentPointerMove(event);
  }

  onMenuPointerMove(event) {
    if (!this.hoverMedia.matches) return;

    const summaryRect = this.summary.getBoundingClientRect();
    const isOverSummary =
      event.clientX >= summaryRect.left &&
      event.clientX <= summaryRect.right &&
      event.clientY >= summaryRect.top &&
      event.clientY <= summaryRect.bottom;

    if (this.classList.contains('menu-closing')) {
      if (isOverSummary) this.cancelClose();
      return;
    }

    if (!this.mainDetailsToggle.open) {
      if (isOverSummary) this.queueOpen();
      return;
    }

  }

  onDocumentPointerMove(event) {
    if (!this.hoverMedia.matches || !this.mainDetailsToggle.open || this.classList.contains('menu-closing')) return;

    const summaryRect = this.summary.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();
    const retentionPadding = 14;
    const bridgePadding = 20;
    const isOverSummary =
      event.clientX >= summaryRect.left - retentionPadding &&
      event.clientX <= summaryRect.right + retentionPadding &&
      event.clientY >= summaryRect.top - retentionPadding &&
      event.clientY <= summaryRect.bottom + retentionPadding;
    const isOverContent =
      event.clientX >= contentRect.left - retentionPadding &&
      event.clientX <= contentRect.right + retentionPadding &&
      event.clientY >= contentRect.top - retentionPadding &&
      event.clientY <= contentRect.bottom + retentionPadding;
    const isOverBridge =
      event.clientX >= summaryRect.left - bridgePadding &&
      event.clientX <= summaryRect.right + bridgePadding &&
      event.clientY > summaryRect.bottom &&
      event.clientY < contentRect.top;

    if (isOverSummary || isOverContent || isOverBridge) {
      this.cancelClose();
    } else {
      this.queueClose();
    }
  }

  queueClose() {
    if (!this.hoverMedia.matches) return;

    if (!this.mainDetailsToggle.open) return;

    this.close();
  }

  close(immediate = false) {
    if (!this.mainDetailsToggle.open) return;

    clearTimeout(this.closeAnimationTimeout);
    document.removeEventListener('pointermove', this.boundDocumentPointerMove);

    if (immediate || !this.hoverMedia.matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      super.close();
      this.classList.remove('menu-closing');
      return;
    }

    this.classList.add('menu-closing');
    this.summary.setAttribute('aria-expanded', false);
    this.closeAnimationTimeout = setTimeout(() => {
      super.close();
      this.classList.remove('menu-closing');
    }, 180);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (this.mainDetailsToggle.open) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }
  }
}

customElements.define('header-menu', HeaderMenu);
